import type { QueryClient } from '@tanstack/react-query';
import type { DependencyType, Item } from '@/common/api/types/items';
import type {
  ScenarioItem,
  TimelineItemItemDependency,
} from '@/common/api/types/planner/timelineItem';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { insightsQueryKeys } from '@/features/insights/queryKeys';
import { plannerQueryKeys } from '@/features/planner/queryKeys';
import { teamsQueryKeys } from '@/features/teams/queryKeys';
import { visualizationQueryKeys } from '@/features/visualizations/queryKeys';

export function patchItemCache(
  queryClient: QueryClient,
  itemId: number,
  patch: Partial<Item>,
): Item | undefined {
  const snapshot = queryClient.getQueryData<Item>(commonQueryKeys.item(itemId));
  if (snapshot) {
    queryClient.setQueryData<Item>(commonQueryKeys.item(itemId), (old) =>
      old ? ({ ...old, ...patch } as Item) : old,
    );
  }
  return snapshot;
}

export function restoreItemCache(
  queryClient: QueryClient,
  itemId: number,
  snapshot: Item | undefined,
): void {
  if (snapshot !== undefined) {
    queryClient.setQueryData(commonQueryKeys.item(itemId), snapshot);
  }
}

export function invalidateSingleItemQueries(queryClient: QueryClient, itemId: number): void {
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.item(itemId) });
  void queryClient.invalidateQueries({
    queryKey: visualizationQueryKeys.loopItem.detail(itemId),
  });
  void queryClient.invalidateQueries({
    queryKey: visualizationQueryKeys.items.partial(),
    refetchType: 'none',
  });
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetchV2.all() });
  void queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetch.all() });
  void queryClient.invalidateQueries({
    queryKey: [commonQueryKeys.itemsTable.visibleItems],
    refetchType: 'none',
  });
}

export function invalidateTeamsCacheIfTeamChanged(
  queryClient: QueryClient,
  patch: Record<string, unknown>,
): void {
  if (!('teamId' in patch)) return;
  void queryClient.invalidateQueries({ queryKey: teamsQueryKeys.all() });
}

export function invalidateAfterDependencyChange(
  queryClient: QueryClient,
  itemAId: number,
  itemBId: number,
): void {
  invalidateSingleItemQueries(queryClient, itemAId);
  invalidateSingleItemQueries(queryClient, itemBId);
  void queryClient.invalidateQueries({ queryKey: insightsQueryKeys.health.byItemId(itemAId) });
  void queryClient.invalidateQueries({ queryKey: insightsQueryKeys.health.byItemId(itemBId) });
  void reconcileAndSettle(queryClient, [itemAId, itemBId]);
}

export function invalidateAfterBulkDependencyChange(
  queryClient: QueryClient,
  itemIds: Iterable<number>,
): void {
  const uniqueIds = new Set(itemIds);
  for (const id of uniqueIds) {
    invalidateSingleItemQueries(queryClient, id);
    void queryClient.invalidateQueries({ queryKey: insightsQueryKeys.health.byItemId(id) });
  }
  void reconcileAndSettle(queryClient, [...uniqueIds]);
}

export function invalidateAfterScenarioDependencyChange(
  queryClient: QueryClient,
  loopId: number,
  scenarioId: number,
): void {
  void queryClient.invalidateQueries({
    queryKey: plannerQueryKeys.timelineItems.root({ loopId, scenarioId }),
  });
}

interface PatchScenarioItemDependencyParams {
  queryClient: QueryClient;
  loopId: number;
  scenarioId: number;
  itemId: number;
  otherItemId: number;
  dependencyType: DependencyType;
  action: 'add' | 'remove';
}

// Writes a server-returned scenario item into the cache, preserving dependency
// edges the cache already knows. Endpoints that return a bare scenario item row
// (the PATCH update, the bulk-edit commit) omit `dependencies` entirely, so a
// wholesale write would blank the item panel's Dependencies tab until the next
// refetch. An explicit [] still clears, since only null/undefined means "absent".
//
// `scenarioId` is caller-supplied rather than read off `item` so the write always
// lands in the cache the caller is rendering, even if a response were to carry a
// different scenarioId than the one requested.
export function writeScenarioItemPreservingDependencies(
  queryClient: QueryClient,
  loopId: number,
  scenarioId: number,
  item: ScenarioItem,
): void {
  const key = plannerQueryKeys.scenarioItems.detail({
    loopId,
    scenarioId,
    itemId: item.itemId,
  });
  queryClient.setQueryData<ScenarioItem>(key, (existing) =>
    item.dependencies == null && existing?.dependencies != null
      ? { ...item, dependencies: existing.dependencies }
      : item,
  );
}

// Adds or removes a single dependency edge on the cached scenario item, keyed
// from the perspective of `itemId`. The scenario table subscribes to this cache
// and syncs the change into its own (instance-scoped) visible rows, so the
// dependency indicator updates without a refetch.
export function patchScenarioItemDependency({
  queryClient,
  loopId,
  scenarioId,
  itemId,
  otherItemId,
  dependencyType,
  action,
}: PatchScenarioItemDependencyParams): void {
  const key = plannerQueryKeys.scenarioItems.detail({ loopId, scenarioId, itemId });
  queryClient.setQueryData<ScenarioItem>(key, (existing) => {
    if (!existing) return existing;
    const withoutEdge = (existing.dependencies ?? []).filter(
      (dep) => !(dep.dependencyItemId === otherItemId && dep.dependencyType === dependencyType),
    );
    const nextDependencies: TimelineItemItemDependency[] =
      action === 'add'
        ? [...withoutEdge, { dependencyType, dependencyItemId: otherItemId }]
        : withoutEdge;
    return { ...existing, dependencies: nextDependencies };
  });
}
